import {getCookie, setCookie} from './cookies'
import {CanonicalStableIdStorageKey, LegacyShopperIdStorageKey, normalizeStableId} from '@/lib/stable-id'

const OneDayMs = 1000 * 60 * 60 * 24

function getFromStoreOrCookie(key: string): unknown {
  const fromStorage = safeGetFromLocalStorage(key)
  if (fromStorage != null) return fromStorage
  const fromCookie = getCookie(key)
  if (fromCookie != null) {
    safeSetToLocalStorage(key, fromCookie)
    return fromCookie
  }
  return null
}

export function safeGetFromLocalStorage(key: string): unknown {
  try {
    const raw = localStorage.getItem(key)
    if (raw == null) return null
    try {
      return JSON.parse(raw)
    } catch {
      return raw
    }
  } catch (error) {
    console.warn('[Anagram] Failed to get from localStorage:', key, error)
    return null
  }
}

export function safeSetToLocalStorage(key: string, value: unknown): boolean {
  try {
    const toStore = typeof value === 'string' ? value : JSON.stringify(value)
    localStorage.setItem(key, toStore)
    return true
  } catch (error) {
    if (error instanceof DOMException) {
      if (error.name === 'QuotaExceededError') {
        console.error('[Anagram] localStorage quota exceeded for key:', key)
      } else if (error.name === 'SecurityError') {
        console.error('[Anagram] Security error accessing localStorage for key:', key)
      } else {
        console.error('[Anagram] Failed to set localStorage:', key, error)
      }
    }
    return false
  }
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function generateSessionId(shopperId: string): string {
  return `${shopperId}==${new Date().toISOString()}`
}

function isSessionValid(shopperId: string, sessionId: string | null): boolean {
  if (!sessionId) return false
  const parts = (sessionId as string).split('==')
  const parsedShopperId = parts.at(0)
  const timestamp = parts.at(1)
  if (!parsedShopperId || !timestamp) return false
  const parsedDate = new Date(timestamp)
  if (Number.isNaN(parsedDate.getTime())) return false
  if (parsedShopperId !== shopperId) return false
  return parsedDate >= new Date(Date.now() - OneDayMs)
}

function getValidStableIdFromStoreOrCookie(key: string): string | null {
  const fromStorage = normalizeStableId(safeGetFromLocalStorage(key))
  if (fromStorage) return fromStorage

  const fromCookie = normalizeStableId(getCookie(key))
  if (fromCookie) {
    safeSetToLocalStorage(key, fromCookie)
    return fromCookie
  }

  return null
}

export function ensureStableId(legacyShopperId?: string | null): string {
  const stableId =
    getValidStableIdFromStoreOrCookie(CanonicalStableIdStorageKey) ??
    normalizeStableId(legacyShopperId) ??
    normalizeStableId(getFromStoreOrCookie(LegacyShopperIdStorageKey)) ??
    generateId()

  safeSetToLocalStorage(CanonicalStableIdStorageKey, stableId)
  setCookie(CanonicalStableIdStorageKey, stableId)

  return stableId
}

export function ensureShopperAndSessionIds(): {shopperId: string; sessionId: string; stableId: string} {
  let shopperId = getFromStoreOrCookie(LegacyShopperIdStorageKey) as string | null
  const sessionId = getFromStoreOrCookie('AnagramSessionId') as string | null
  if (!shopperId) {
    shopperId = generateId()
    safeSetToLocalStorage(LegacyShopperIdStorageKey, shopperId)
  }
  const stableId = ensureStableId(shopperId)
  let resolvedSessionId = sessionId
  if (!isSessionValid(shopperId, resolvedSessionId)) {
    resolvedSessionId = generateSessionId(shopperId)
    safeSetToLocalStorage('AnagramSessionId', resolvedSessionId)
  }
  const ensuredSessionId = resolvedSessionId ?? generateSessionId(shopperId)

  const lastUsedAt = getFromStoreOrCookie('AnagramLastUsedAt')
  const activity = getFromStoreOrCookie('AnagramExperienceActivity')

  // Re-set on every load to keep refreshing Safari ITP's 7-day cap on
  // document.cookie-set cookies.
  setCookie(LegacyShopperIdStorageKey, shopperId)
  setCookie('AnagramSessionId', ensuredSessionId)
  if (lastUsedAt != null) setCookie('AnagramLastUsedAt', lastUsedAt)
  if (activity != null) setCookie('AnagramExperienceActivity', activity)

  return {shopperId, sessionId: ensuredSessionId, stableId}
}

function parseIso(iso: string): Date | null {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

function getActivityMap(): Record<string, string> {
  const raw = getFromStoreOrCookie('AnagramExperienceActivity')
  if (!raw || typeof raw !== 'object') return {}
  const out: Record<string, string> = {}
  for (const k in raw as Record<string, unknown>) {
    if (Object.hasOwn(raw as object, k)) {
      const v = (raw as Record<string, unknown>)[k]
      if (typeof v === 'string' && parseIso(v)) {
        out[k] = v
      }
    }
  }
  return out
}

const MaxActivityEntries = 20

export function upsertExperienceActivity(experienceId: string, lastUsedDateIso: string): void {
  const map = getActivityMap()
  const now = Date.now()
  for (const k in map) {
    if (Object.hasOwn(map, k)) {
      const value = map[k]
      if (!value) continue
      const entryDate = parseIso(value)
      if (!entryDate || now - entryDate.getTime() > OneDayMs) {
        delete map[k]
      }
    }
  }
  map[experienceId] = lastUsedDateIso || new Date().toISOString()

  const keys = Object.keys(map)
  if (keys.length > MaxActivityEntries) {
    const sorted = keys.sort((a, b) => {
      const da = parseIso(map[a] ?? '')?.getTime() ?? 0
      const db = parseIso(map[b] ?? '')?.getTime() ?? 0
      return da - db
    })
    for (const k of sorted.slice(0, keys.length - MaxActivityEntries)) {
      delete map[k]
    }
  }

  safeSetToLocalStorage('AnagramExperienceActivity', map)
  setCookie('AnagramExperienceActivity', map)
}
